// ============================================================
// AgentFloat — エージェントのフロート表示（S1・2026-08-25）
//
// 正: booking-platform/md/ai-control-center.md §16-4
//
// - パネル: ヘッダをつかんでドラッグ移動できる
// - 閉じる: 消えるのではなく **直径20pxの丸ボタン ●** に最小化
//   （● もドラッグ移動でき、クリックで復元）
// - 位置・最小化状態は localStorage に保存（floatState.ts）
//
// ドラッグはライブラリを使わず pointer events で実装。
// クリックとドラッグの区別は移動量 5px のしきい値で行う
// （● はクリックで開くため、区別しないとドラッグのたびに開いてしまう）。
// ============================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import AgentPanel from './AgentPanel';
import { useAgent } from '../hooks/useAgent';
import { loadFloatState, saveFloatState, clampPos, FloatPos } from '../agent/floatState';

const BUBBLE_SIZE = 40;               // 直径40px（当初20pxは小さすぎた — 2026-08-25 前田さん指示で2倍に）
const PANEL_W = 420;                  // AgentPanel の幅と一致させる
const DRAG_THRESHOLD = 5;             // これ未満の移動はクリック扱い

/** 既定位置（保存が無いとき）。パネル=右上 / ●=右下 */
const defaultBubblePos = (): FloatPos => ({
  x: window.innerWidth - BUBBLE_SIZE - 24,
  y: window.innerHeight - BUBBLE_SIZE - 24,
});

export default function AgentFloat() {
  const agent = useAgent();
  const [state, setState] = useState(() => loadFloatState());

  // ドラッグ中の一時位置（ドラッグ確定まで localStorage に書かない）
  const dragRef = useRef<{
    kind: 'panel' | 'bubble';
    startX: number; startY: number;      // pointerdown 時のポインタ位置
    origX: number; origY: number;        // pointerdown 時の要素位置
    moved: boolean;
  } | null>(null);
  const [dragPos, setDragPos] = useState<FloatPos | null>(null);

  const persist = useCallback((next: Partial<ReturnType<typeof loadFloatState>>) => {
    setState((cur) => {
      const merged = { ...cur, ...next };
      saveFloatState(merged);
      return merged;
    });
  }, []);

  // ---- ドラッグ共通処理 ----
  const beginDrag = useCallback((kind: 'panel' | 'bubble', e: React.PointerEvent, orig: FloatPos) => {
    // ヘッダ内のボタン類（新規会話・最小化）からのドラッグ開始は無視する
    if ((e.target as HTMLElement).closest('button')) return;
    dragRef.current = { kind, startX: e.clientX, startY: e.clientY, origX: orig.x, origY: orig.y, moved: false };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
    d.moved = true;
    const size = d.kind === 'bubble' ? BUBBLE_SIZE : PANEL_W;
    setDragPos(clampPos({ x: d.origX + dx, y: d.origY + dy }, size, d.kind === 'bubble' ? BUBBLE_SIZE : 200));
  }, []);

  const endDrag = useCallback((onClick?: () => void) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (d.moved && dragPos) {
      persist(d.kind === 'panel' ? { panelPos: dragPos } : { bubblePos: dragPos });
    } else if (!d.moved && onClick) {
      onClick();               // 動いていない = クリック
    }
    setDragPos(null);
  }, [dragPos, persist]);

  // 画面リサイズで位置が画面外に出たら次回描画で clamp される（描画時に毎回 clamp）
  useEffect(() => {
    const onResize = () => setState((cur) => ({ ...cur }));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 予約セッションへの切替要求（AI ツール / mail-queue のカードから届く。§16 S3）
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const code = String(detail.contact_code || '').trim();
      if (!code) return;
      e.preventDefault();   // 「受領した」をディスパッチ側（ツール）へ返す
      persist({ minimized: false });   // ● のときはパネルを開く
      // 実際に切り替わったかをツールへ返す（detail.done。2026-10-15）
      void agent.openReservationSession(code, detail.label, detail.mail)
        .then((ok: boolean) => detail.done?.(ok), () => detail.done?.(false));
    };
    window.addEventListener('agent:open-reservation-session', onOpen);
    return () => window.removeEventListener('agent:open-reservation-session', onOpen);
  }, [agent, persist]);

  // ---- 最小化: ● ----
  if (state.minimized) {
    const saved = state.bubblePos ?? defaultBubblePos();
    const pos = clampPos(dragRef.current?.kind === 'bubble' && dragPos ? dragPos : saved, BUBBLE_SIZE, BUBBLE_SIZE);
    return (
      <Tooltip title="エージェントを開く" placement="left">
        <Box
          role="button"
          aria-label="エージェントを開く"
          onPointerDown={(e) => beginDrag('bubble', e, pos)}
          onPointerMove={onPointerMove}
          onPointerUp={() => endDrag(() => persist({ minimized: false }))}
          sx={{
            position: 'fixed',
            left: pos.x,
            top: pos.y,
            width: BUBBLE_SIZE,
            height: BUBBLE_SIZE,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 2px 8px rgba(99, 102, 241, 0.5)',
            cursor: 'grab',
            zIndex: 10500,
            touchAction: 'none',
            // 20px は小さいので、当たり判定だけ広げる（見た目は 20px のまま）
            '&::after': { content: '""', position: 'absolute', inset: -8 },
            '&:hover': { boxShadow: '0 2px 12px rgba(99, 102, 241, 0.8)' },
          }}
        />
      </Tooltip>
    );
  }

  // ---- パネル ----
  const panelPos = state.panelPos
    ? clampPos(dragRef.current?.kind === 'panel' && dragPos ? dragPos : state.panelPos, PANEL_W, 200)
    : null;   // null = AgentPanel の既定位置（右上）

  return (
    <AgentPanel
      agent={agent}
      onClose={() => persist({ minimized: true })}
      floatPos={panelPos}
      dragHandleProps={{
        onPointerDown: (e: React.PointerEvent) =>
          beginDrag('panel', e, panelPos ?? { x: window.innerWidth - PANEL_W - 16, y: 80 }),
        onPointerMove,
        onPointerUp: () => endDrag(),
        style: { cursor: 'grab', touchAction: 'none' },
      }}
    />
  );
}
